import { useEffect, useState } from 'react';
import intl from 'react-intl-universal';
import { useSnackbar } from 'notistack';
import { Typography, Stack } from '@mui/material';
import Grid from '@mui/material/Grid2';
import api from 'utils/api';
import { PageLoader, PageError } from 'components';
import localData from 'utils/localData';
import logger from 'utils/logger';

import { USER_DATA } from 'shared/constants/users';
import { DOLPHIN_API } from 'shared/constants/apis';
import { ServiceType } from 'shared/constants/service';
import { NotificationTypeIntlKey } from './constants';
import SwitchWithLabels from './Switch';

// Create a logger instance for this component
const log = logger.createLogger('NotificationTab');

const NotificationPreference = () => {
  const currentOrganizationId = localData.get(
    USER_DATA.CURRENT_ORGANIZATION_ID
  );
  const userId = localData.get(USER_DATA.USER_ID);

  const [preferencesSeimitsu, setPreferencesSeimitsu] = useState({});
  const [preferencesKenshin, setPreferencesKenshin] = useState({});
  const [error, setError] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  const handleError = (error) => {
    log.error('Error occurred', { error });
  };

  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        const response = await api.get(DOLPHIN_API.NOTIFICATION_PREFERENCE, {
          params: {
            organizationId: currentOrganizationId,
            userId: userId,
            service: ServiceType.SEIMITSU,
          },
        });
        setPreferencesSeimitsu(response.preferences);
      } catch (err) {
        setError(err);
      }
    };
    fetchPreferences();
  }, [currentOrganizationId, userId]);

  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        const response = await api.get(DOLPHIN_API.NOTIFICATION_PREFERENCE, {
          params: {
            organizationId: currentOrganizationId,
            userId: userId,
            service: ServiceType.KENSHIN,
          },
        });
        setPreferencesKenshin(response.preferences);
      } catch (err) {
        setError(err);
      }
    };
    fetchPreferences();
  }, [currentOrganizationId, userId]);

  if (error)
    return (
      <PageError
        errorCode={error.response.status}
        customErrorMessage={error.response.data.message}
        customErrorDescription={error.response.data.description}
      />
    );
  // If either preference is still null, show the loader
  if (
    Object.keys(preferencesSeimitsu).length === 0 ||
    Object.keys(preferencesKenshin).length === 0
  ) {
    return <PageLoader />;
  }
  const orderedNotificationKeysSeimitsu = Object.keys(preferencesSeimitsu).sort(
    (a, b) =>
      preferencesSeimitsu[a].displayIndex - preferencesSeimitsu[b].displayIndex
  );
  const orderedNotificationKeysKenshin = Object.keys(preferencesKenshin).sort(
    (a, b) =>
      preferencesKenshin[a].displayIndex - preferencesKenshin[b].displayIndex
  );

  const handleChangePreference = async (
    service,
    notificationKey,
    channel,
    checked
  ) => {
    const variables = {
      params: {
        organizationId: currentOrganizationId,
        userId: userId,
        service: service,
      },
      data: {
        preferences: {
          [notificationKey]: {
            [channel]: checked,
          },
        },
      },
    };
    api.put(DOLPHIN_API.NOTIFICATION_PREFERENCE, variables).then(
      () => {
        if (service === ServiceType.SEIMITSU) {
          setPreferencesSeimitsu((prevState) => ({
            ...prevState,
            [notificationKey]: {
              ...prevState[notificationKey],
              [channel]: checked,
            },
          }));
        }
      },
      (error) => {
        handleError(error);
        enqueueSnackbar('Failed to update settings.', {
          variant: 'success',
          autoHideDuration: 3000,
        });
      }
    );
  };

  return (
    <Grid
      container
      justifyContent="space-between"
      sx={{ marginTop: '30px', width: '100%' }}>
      <Grid size={12}>
        <Stack data-testid="notification-preference-content">
          <Grid container spacing={7}>
            <Grid size={8}>
              <Typography fontSize={18} fontWeight={500} textAlign="left">
                {intl.get('personal_settings_notification_title_text_seimitsu')}
              </Typography>
            </Grid>
            <Grid>
              <Typography fontSize={18} fontWeight={500} textAlign="center">
                {intl.get('personal_settings_notification_channel_inapp')}
              </Typography>
            </Grid>
          </Grid>

          {orderedNotificationKeysSeimitsu.map((notificationKey) => {
            if (!(notificationKey in preferencesSeimitsu)) return null;
            const intlKey = NotificationTypeIntlKey[notificationKey];
            return (
              <Grid container key={notificationKey} sx={{ margin: 1 }}>
                <Grid size={8}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="flex-start">
                    <Typography variant="subtitle1" gutterBottom>
                      {intl.get(intlKey)}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center">
                    <SwitchWithLabels
                      preferences={preferencesSeimitsu}
                      service={ServiceType.SEIMITSU}
                      notificationKey={notificationKey}
                      channel="inapp"
                      handleChangePreference={handleChangePreference}
                    />
                  </Stack>
                </Grid>
              </Grid>
            );
          })}

          <Grid container spacing={7} sx={{ marginTop: '20px' }}>
            <Grid size={8}>
              <Typography fontSize={18} fontWeight={500} textAlign="left">
                {intl.get('personal_settings_notification_title_text_kenshin')}
              </Typography>
            </Grid>
            <Grid>
              <Typography fontSize={18} fontWeight={500} textAlign="center">
                {intl.get('personal_settings_notification_channel_inapp')}
              </Typography>
            </Grid>
          </Grid>

          {orderedNotificationKeysKenshin.map((notificationKey) => {
            if (!(notificationKey in preferencesKenshin)) return null;
            const intlKey = NotificationTypeIntlKey[notificationKey];
            return (
              <Grid container key={notificationKey} sx={{ margin: 1 }}>
                <Grid size={8}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="flex-start">
                    <Typography variant="subtitle1" gutterBottom>
                      {intl.get(intlKey)}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center">
                    <SwitchWithLabels
                      preferences={preferencesKenshin}
                      service={ServiceType.KENSHIN}
                      notificationKey={notificationKey}
                      channel="inapp"
                      handleChangePreference={handleChangePreference}
                    />
                  </Stack>
                </Grid>
              </Grid>
            );
          })}
        </Stack>
      </Grid>
    </Grid>
  );
};

export default NotificationPreference;
